import { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Link,
  Stack,
  Typography,
  useTheme,
} from '@mui/material';
import { CloseOutlined } from '@mui/icons-material';
import { LoadingSpinner } from '../../presentation-components/display-elements-components/loading-spinner';
import SurveyAdminService from '../../../shared/services/survey/survey-admin.service';
import type {
  ISurveyProvisionalEmissionAggregateRow,
  ISurveyProvisionalEmissionRow,
  IProvisionalAdjustmentContribution,
} from '../../../shared/services/survey/survey.model';

function fmtNum(n: number | null | undefined, digits = 6): string {
  if (n == null || !Number.isFinite(n)) return '—';
  if (Math.abs(n) >= 1) return n.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: digits });
  return n.toLocaleString(undefined, { maximumSignificantDigits: 8 });
}

const skipLabels: Record<string, string> = {
  conditions_not_met: 'Rule conditions not met',
  formula_undefined_or_non_numeric: 'Could not evaluate formula (missing or non‑numeric inputs)',
};

function positiveKg(v: number | null | undefined): boolean {
  return v != null && Number.isFinite(v) && v > 0;
}

/** Rules with emissions first (largest first), the rest in priority order. */
function sortRowsByKg<T extends { priority?: number }>(list: T[], kgOf: (row: T) => number | null | undefined): T[] {
  const withKg = list.filter(r => positiveKg(kgOf(r))).sort((a, b) => (kgOf(b) ?? 0) - (kgOf(a) ?? 0));
  const withoutKg = list.filter(r => !positiveKg(kgOf(r))).sort((a, b) => (a.priority ?? 0) - (b.priority ?? 0));
  return [...withKg, ...withoutKg];
}

function AdjustmentContributions({ contributions, showOutputs }: {
  contributions?: IProvisionalAdjustmentContribution[];
  showOutputs?: boolean;
}) {
  if (!contributions || contributions.length === 0) return null;
  return (
    <Box sx={{ mt: 1, pl: 1.5, borderLeft: '2px solid', borderColor: 'divider' }}>
      <Typography variant='caption' color='text.secondary' sx={{ fontStyle: 'italic' }}>
        Adjustment rules used
      </Typography>
      <Stack spacing={0.25} sx={{ mt: 0.25 }}>
        {contributions.map((adj) => (
          <Typography key={adj.alias} variant='body2'>
            <Typography component='span' color='text.secondary'>{adj.name || adj.alias}</Typography>
            {showOutputs && adj.output != null && (
              <Typography component='span' fontWeight={600} sx={{ ml: 0.75 }}>
                = {fmtNum(adj.output)}
              </Typography>
            )}
          </Typography>
        ))}
      </Stack>
    </Box>
  );
}

/** The Conversion / Source / Multiplier metadata lines shared by both row shapes. */
function ConversionMetaLines({ row }: {
  row: Pick<ISurveyProvisionalEmissionRow, 'conversionDetailSummary' | 'conversionSourceLabel' | 'calendarYear' | 'multiplier'>;
}) {
  return (
    <>
      <Typography variant='body2'>
        <Typography component='span' variant='caption' color='text.secondary'>Conversion&nbsp;</Typography>
        {row.conversionDetailSummary ?? '—'}
      </Typography>
      <Typography variant='body2'>
        <Typography component='span' variant='caption' color='text.secondary'>Source / year&nbsp;</Typography>
        {row.conversionSourceLabel ?? '—'}
        {row.calendarYear ? ` · ${row.calendarYear}` : ''}
      </Typography>
      <Typography variant='body2'>
        <Typography component='span' variant='caption' color='text.secondary'>Multiplier (kg CO₂e per activity unit)&nbsp;</Typography>
        {fmtNum(row.multiplier)}
      </Typography>
    </>
  );
}

function RuleOutputLine({ label, value, unitName }: { label: string; value: number | null | undefined; unitName?: string | null }) {
  return (
    <Typography variant='body2'>
      <Typography component='span' variant='caption' color='text.secondary'>{label}&nbsp;</Typography>
      <Typography component='span' fontWeight={600}>{fmtNum(value)}</Typography>
      {unitName ? (
        <Typography component='span' variant='caption' color='text.secondary' sx={{ ml: 0.5 }}>
          {unitName}
        </Typography>
      ) : null}
    </Typography>
  );
}

function KgLine({ label, kg }: { label: string; kg: number | null | undefined }) {
  return (
    <Typography variant='body2'>
      <Typography component='span' variant='caption' color='text.secondary'>{label}&nbsp;</Typography>
      <Typography component='span' fontWeight={700} color='primary.main'>
        {kg != null ? `${fmtNum(kg)} kg CO₂e` : '—'}
      </Typography>
    </Typography>
  );
}

function SessionRowBody({ row }: { row: ISurveyProvisionalEmissionRow }) {
  const hasKg = row.applies && positiveKg(row.provisionalKgCo2e);
  if (!row.applies) {
    return (
      <Typography variant='body2' color='text.secondary' sx={{ mt: 1 }}>
        {row.skipReason ? (skipLabels[row.skipReason] ?? row.skipReason) : 'Not applied'}
      </Typography>
    );
  }
  return (
    <Stack spacing={0.75} sx={{ mt: 1 }}>
      <RuleOutputLine label='Rule output (activity)' value={row.formulaOutput} unitName={row.activityUnitName} />
      {hasKg && <ConversionMetaLines row={row} />}
      <KgLine label='Provisional emissions' kg={row.provisionalKgCo2e} />
      {!hasKg && row.multiplier != null && row.formulaOutput != null && row.provisionalKgCo2e == null && row.conversionId ? (
        <Typography variant='caption' color='warning.main'>
          Unable to derive kg CO₂e from this preview.
        </Typography>
      ) : null}
      {!hasKg && !row.conversionId && row.formulaOutput != null ? (
        <Typography variant='caption' color='text.secondary'>
          This rule has no conversion factor attached.
        </Typography>
      ) : null}
      <AdjustmentContributions contributions={row.adjustmentContributions} showOutputs />
    </Stack>
  );
}

function AggregateRowBody({ row }: { row: ISurveyProvisionalEmissionAggregateRow }) {
  const hasKg = positiveKg(row.totalProvisionalKgCo2e);
  const appliedLine = (
    <Typography variant='body2' color='text.secondary'>
      Applied in <strong>{row.responsesApplied}</strong> of {row.sessionCountIncluded} response
      {row.sessionCountIncluded === 1 ? '' : 's'}{hasKg ? '' : '.'}
    </Typography>
  );
  if (!hasKg) {
    return (
      <>
        <Box sx={{ mt: 1 }}>
          {appliedLine}
          {row.responsesApplied > 0 && row.responsesWithEmissionsKg === 0 && (
            <Typography variant='caption' color='text.secondary' display='block' sx={{ mt: 0.75 }}>
              No conversion factor on this rule, or rule output could not be converted to kg CO₂e for these responses.
            </Typography>
          )}
        </Box>
        {row.responsesApplied > 0 && (
          <AdjustmentContributions contributions={row.adjustmentContributions} />
        )}
      </>
    );
  }
  return (
    <Stack spacing={0.75} sx={{ mt: 1 }}>
      {appliedLine}
      {row.responsesWithEmissionsKg > 0 ? (
        <Typography variant='body2' color='text.secondary'>
          With a kg CO₂e estimate: <strong>{row.responsesWithEmissionsKg}</strong>
        </Typography>
      ) : null}
      <RuleOutputLine label='Sum of rule outputs (activity)' value={row.totalFormulaOutput} unitName={row.activityUnitName} />
      <ConversionMetaLines row={row} />
      <KgLine label='Combined provisional emissions' kg={row.totalProvisionalKgCo2e} />
      <AdjustmentContributions contributions={row.adjustmentContributions} />
    </Stack>
  );
}

type Props = {
  open: boolean;
  onClose: () => void;
  /** 'session' shows one response's provisional emissions; 'aggregate' combines all sessions. */
  mode: 'session' | 'aggregate';
  /** Required when mode is 'aggregate'. */
  surveyId?: string | null;
  /** Required when mode is 'session'. */
  sessionId?: string | null;
  respondentLabel?: string;
};

export const SurveyEmissionsBreakdownDialog = ({
  open,
  onClose,
  mode,
  surveyId,
  sessionId,
  respondentLabel,
}: Props) => {
  const theme = useTheme();
  const [loading, setLoading] = useState(false);
  const [sessionRows, setSessionRows] = useState<ISurveyProvisionalEmissionRow[]>([]);
  const [aggregateRows, setAggregateRows] = useState<ISurveyProvisionalEmissionAggregateRow[]>([]);
  const [sessionCount, setSessionCount] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [invalidatedMessage, setInvalidatedMessage] = useState<string | null>(null);
  const [showNotApplied, setShowNotApplied] = useState(false);

  const targetId = mode === 'session' ? sessionId : surveyId;

  useEffect(() => {
    if (!open || !targetId) {
      setSessionRows([]);
      setAggregateRows([]);
      setSessionCount(0);
      setError(null);
      setLoading(false);
      setInvalidatedMessage(null);
      setShowNotApplied(false);
      return;
    }
    let cancelled = false;
    const svc = SurveyAdminService.getInstance();
    setLoading(true);
    setError(null);
    const request = mode === 'session'
      ? svc.getSessionProvisionalEmissions(targetId).then((data) => {
        if (cancelled) return;
        if (data.invalidated) {
          setSessionRows([]);
          setInvalidatedMessage(
            data.message ?? 'This response has been invalidated and is excluded from provisional emissions.',
          );
        } else {
          setInvalidatedMessage(null);
          setSessionRows(data.rows ?? []);
        }
      })
      : svc.getSurveyProvisionalEmissionsAggregate(targetId).then((data) => {
        if (cancelled) return;
        setSessionCount(typeof data.sessionCount === 'number' ? data.sessionCount : 0);
        setAggregateRows(data.rows ?? []);
      });
    request
      .catch(() => {
        if (!cancelled) setError('Could not load provisional emissions.');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, [open, mode, targetId]);

  const sortedSessionRows = useMemo(() => sortRowsByKg(sessionRows, r => (r.applies ? r.provisionalKgCo2e : null)), [sessionRows]);
  const sortedAggregateRows = useMemo(() => sortRowsByKg(aggregateRows, r => r.totalProvisionalKgCo2e), [aggregateRows]);

  const isSession = mode === 'session';
  const rowCount = isSession ? sessionRows.length : aggregateRows.length;

  const appliesTo = (rowIsApplied: boolean) => (isSession
    ? sortedSessionRows.filter(r => r.applies === rowIsApplied)
    : sortedAggregateRows.filter(r => (r.responsesApplied > 0) === rowIsApplied));
  const appliedRows = appliesTo(true);
  const notAppliedRows = appliesTo(false);
  const visibleRows = showNotApplied ? [...appliedRows, ...notAppliedRows] : appliedRows;

  const totalKgAcrossRules = aggregateRows.reduce((s, r) => (positiveKg(r.totalProvisionalKgCo2e) ? s + (r.totalProvisionalKgCo2e as number) : s), 0);

  return (
    <Dialog open={open} onClose={onClose} maxWidth='md' fullWidth aria-labelledby='emissions-breakdown-title'>
      <DialogTitle id='emissions-breakdown-title' sx={{ pr: 5 }}>
        <Stack direction='row' alignItems='center' justifyContent='space-between'>
          <Typography variant='h6' component='span' fontWeight={600}>
            {isSession
              ? `Provisional emissions${respondentLabel ? ` · ${respondentLabel}` : ''}`
              : 'Combined provisional emissions'}
          </Typography>
          <IconButton onClick={onClose} aria-label='Close' sx={{ mr: -0.75 }}>
            <CloseOutlined />
          </IconButton>
        </Stack>
      </DialogTitle>
      <DialogContent>
        <Typography variant='body2' color='text.secondary' sx={{ mb: 2 }}>
          {isSession
            ? 'Estimated from saved answers using this survey’s calculation rules. Amounts use rule output × conversion factor multiplier (kg CO₂e).'
            : (
              <>
                Totals across <strong>{sessionCount}</strong> response session
                {sessionCount === 1 ? '' : 's'}
                {' '}listed for this survey, split by rule (rule output summed per rule, kg CO₂e = sum of rule × multiplier per response).
              </>
            )}
        </Typography>
        {!isSession && sessionCount > 0 && aggregateRows.some(r => positiveKg(r.totalProvisionalKgCo2e)) && (
          <Typography variant='body2' sx={{ mb: 2 }}>
            <Typography component='span' variant='caption' color='text.secondary'>Total (all rules with emissions)&nbsp;</Typography>
            <Typography component='span' fontWeight={700} color='primary.main'>
              {fmtNum(totalKgAcrossRules)} kg CO₂e
            </Typography>
          </Typography>
        )}
        {loading && (
          <Box sx={{ py: 4, display: 'flex', justifyContent: 'center' }}>
            <LoadingSpinner />
          </Box>
        )}
        {error && !loading && (
          <Typography color='error.main' variant='body2'>{error}</Typography>
        )}
        {!loading && !error && invalidatedMessage && (
          <Typography color='warning.main' variant='body2' sx={{ fontWeight: 500 }}>
            {invalidatedMessage}
          </Typography>
        )}
        {!loading && !error && !invalidatedMessage && rowCount === 0 && (
          <Typography color='text.secondary' variant='body2'>
            {!isSession && sessionCount === 0
              ? 'No response sessions yet.'
              : 'No calculation rules are configured for this survey.'}
          </Typography>
        )}
        {!loading && !error && !invalidatedMessage && rowCount > 0 && (
          <Stack spacing={2}>
            {visibleRows.map(row => (
              <Box
                key={row.ruleId}
                sx={{
                  p: 2,
                  borderRadius: 1,
                  border: '1px solid',
                  borderColor: 'divider',
                  bgcolor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.03)' : 'grey.50',
                }}
              >
                <Typography variant='subtitle2' fontWeight={600}>
                  {row.ruleName || 'Unnamed rule'}
                  <Typography component='span' variant='caption' color='text.secondary' sx={{ ml: 1 }}>
                    priority {row.priority}
                  </Typography>
                </Typography>
                {isSession
                  ? <SessionRowBody row={row as ISurveyProvisionalEmissionRow} />
                  : <AggregateRowBody row={row as ISurveyProvisionalEmissionAggregateRow} />}
              </Box>
            ))}
            {notAppliedRows.length > 0 && (
              <Link
                component='button'
                variant='body2'
                underline='hover'
                onClick={() => setShowNotApplied(v => !v)}
                sx={{ alignSelf: 'flex-start', cursor: 'pointer' }}
              >
                {showNotApplied
                  ? `Hide rules not applied (${notAppliedRows.length})`
                  : `Show rules not applied (${notAppliedRows.length})`}
              </Link>
            )}
          </Stack>
        )}
      </DialogContent>
    </Dialog>
  );
};
